import React, { useState } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import Strings from '../../../Strings';
import { DatePickerField, Input, SelectField, TextArea } from '../../../containers/Form';
import { DeviceInvoicesTypes, getGraphDataForPriceHistory, getPricingColumns, getPricingTypeLabel, INVOICE_STATUSES, isDeviceBasedPricingType, validateInvoice } from './helpers';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { getTooltipFromValidationErrors } from '../../../containers/Modal/Wizard';
import Table3 from '../../../containers/Table/Table3';
import GenericLineGraph from '../../../components/Graphs/GenericLineGraph';

export const EDIT_INVOICE_MODAL_NAME = 'edit-invoice-modal';
export const GENERATE_RECURRING_INVOICES_MODAL_NAME = 'generate-recurring-invoices-modal';
export const SET_INVOICE_DATE_MODAL_NAME = 'set-invoice-date-modal';
export const EDIT_PRICE_MODAL_NAME = 'edit-price-modal';
export const PRICE_HISTORY_MODAL_NAME = 'price-history-modal';

const getErrorsForTooltip = (validationErrors, property) => (
  validationErrors.find(v => v.property === property)?.errors || []
);

export function EditInvoiceModal({ invoice, isRecurring, onClose, onSubmit, organizations }) {
  const [invoiceData, setInvoiceData] = useState(
    invoice ? {
      ...invoice,
      invoice_date: invoice.invoice_date? moment(invoice.invoice_date) : null,
      shipment_date_from_rxcap: invoice.shipment_date_from_rxcap ? moment(invoice.shipment_date_from_rxcap) : null,
      billing_end_date: invoice.billing_end_date ? moment(invoice.billing_end_date) : null,
      comment: invoice.comment || '',
    }
    : null
  );
  const [validationErrors, setValidationErrors] = useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setValidationErrors(validateInvoice(invoiceData, true, isRecurring));
  }, [invoiceData, isRecurring]);

  const handleSubmit = async () => {
    setIsSaving(true);
    const success = await onSubmit({
      ...invoiceData,
      status: invoiceData?.billing_status !== invoice?.billing_status ? invoiceData?.billing_status : undefined,
    });
    if (!success) {
      setIsSaving(false);
    }
  };

  return (
    <SimpleModal
      name={EDIT_INVOICE_MODAL_NAME}
      title={Strings.editInvoice || "Edit Invoice"}
      onCancel={onClose}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSaving || !invoiceData || validationErrors.length > 0}
      emptyFieldsCount={validationErrors.filter(e => e.missing).length}
      saveButtonTooltip={getTooltipFromValidationErrors(validationErrors)}
      onNextButtonHover={() => setHighlightMissingFields(true)}
      content={(
        <>
          <Input
            name="price"
            label={Strings.deviceInvoices.price}
            value={invoiceData?.price}
            onChange={e => setInvoiceData({ ...invoiceData, price: e.target.value })}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={getErrorsForTooltip(validationErrors, 'price')}
          />
          {!isRecurring && (
            <>
              <Input
                name="kitting_fee"
                type="number"
                label={Strings.deviceInvoices.kittingFee}
                value={isNaN(invoiceData?.kitting_fee) ? '' : invoiceData?.kitting_fee}
                onChange={e => setInvoiceData({ ...invoiceData, kitting_fee: e.target.value })}
                isRequired
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={getErrorsForTooltip(validationErrors, 'kitting_fee')}
              />
              <Input
                name="shipping_fee"
                type="number"
                label={Strings.shipping.shippingCost || "Shipping Cost"}
                value={isNaN(invoiceData?.shipping_fee) ? '' : invoiceData?.shipping_fee}
                onChange={e => setInvoiceData({ ...invoiceData, shipping_fee: e.target.value })}
                isRequired
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={getErrorsForTooltip(validationErrors, 'shipping_fee')}
              />
            </>
          )}
          {isRecurring && (
            <Input
              name="data_fee"
              type="number"
              label={Strings.deviceInvoices.dataFee}
              value={isNaN(invoiceData?.data_fee) ? '' : invoiceData?.data_fee}
              onChange={e => setInvoiceData({ ...invoiceData, data_fee: e.target.value })}
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={getErrorsForTooltip(validationErrors, 'data_fee')}
            />
          )}
          <DatePickerField
            name="invoice_date"
            label={Strings.deviceInvoices.invoiceDate || "Invoice Date"}
            value={invoiceData?.invoice_date}
            onChange={date => setInvoiceData({ ...invoiceData, invoice_date: date })}
            highlightInvalid={highlightInvalidFields}
          />
          <SelectField
            name="provider"
            label={Strings.capPatient.provider || 'Provider'}
            value={invoiceData?.provider_id}
            onChange={option => {
              setInvoiceData({ ...invoiceData, provider_id: option.value });
            }}
            data={organizations.map(org => ({ value: org.masked_id, label: org.name })) || []}
            isSearchable
            highlightInvalid={highlightInvalidFields}
          />
          {!isRecurring ? (
            <DatePickerField
              name="shipment_date_from_rxcap"
              label={Strings.shipmentDate || "Shipment Date"}
              value={invoiceData?.shipment_date_from_rxcap ? moment(invoiceData.shipment_date_from_rxcap) : null}
              onChange={date => setInvoiceData({ ...invoiceData, shipment_date_from_rxcap: date })}
              highlightInvalid={highlightInvalidFields}
            />
          ) : (
            <DatePickerField
              name="billing_end_date"
              label={Strings.deviceInvoices.billingEndDate || "Billing End Date"}
              value={invoiceData?.billing_end_date ? moment(invoiceData.billing_end_date) : null}
              onChange={date => setInvoiceData({ ...invoiceData, billing_end_date: date })}
              highlightInvalid={highlightInvalidFields}
            />
          )}
          <SelectField
            name="billing_status"
            label={Strings.status}
            value={invoiceData?.billing_status}
            onChange={option => {
              setInvoiceData({ ...invoiceData, billing_status: option.value });
            }}
            data={Object.values(INVOICE_STATUSES).map(v => ({ value: v.key, label: v.name }))}
            highlightInvalid={highlightInvalidFields}
          />
          <TextArea
            name="comment"
            label={Strings.addComment}
            value={invoiceData?.comment}
            onChange={e => setInvoiceData({ ...invoiceData, comment: e.target.value })}
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={getErrorsForTooltip(validationErrors, 'comment')}
            isRequired
          />
        </>
      )}
    />
  );
}

export function GenerateRecurringInvoicesModal({ onClose, onSubmit }) {
  const [selectedMonth, setSelectedMonth] = useState(moment().startOf('month'));
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const validationErrors = selectedMonth ? [] : [{
    property: 'invoice_date',
    propertyName: Strings.deviceInvoices.invoiceDate,
    missing: true,
    errors: [],
  }];

  const handleSubmit = async () => {
    setIsSaving(true);
    const success = await onSubmit(selectedMonth);
    if (!success) {
      setIsSaving(false);
    }
  };

  return (
    <SimpleModal
      name={GENERATE_RECURRING_INVOICES_MODAL_NAME}
      title={Strings.deviceInvoices.generateInvoicesForMonth || "Generate Invoices for Month"}
      onCancel={onClose}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSaving || !selectedMonth}
      emptyFieldsCount={validationErrors.filter(e => e.missing).length}
      saveButtonTooltip={getTooltipFromValidationErrors(validationErrors)}
      onNextButtonHover={() => setHighlightMissingFields(true)}
      content={(
        <DatePickerField
          name="invoice_date"
          label={Strings.deviceInvoices.invoiceDate || "Invoice Date"}
          value={selectedMonth}
          onChange={date => setSelectedMonth(date)}
          maxDate={moment().endOf('month')}
          openTo="month"
          views={['year', 'month']}
          actions={[]}
          isRequired
          highlightInvalid={highlightInvalidFields}

        />
      )}
    />
  );
}

export function SetInvoiceDateModal({ onClose, onSubmit, title }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSubmit = async () => {
    setIsSaving(true);
    const success = await onSubmit(selectedDate);
    if (!success) {
      setIsSaving(false);
    }
  };

  return (
    <SimpleModal
      name={SET_INVOICE_DATE_MODAL_NAME}
      title={title || Strings.deviceInvoices.setInvoiceDate || "Set Invoice Date"}
      onCancel={onClose}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSaving || !selectedDate}
      emptyFieldsCount={selectedDate ? 0 : 1}
      onNextButtonHover={() => setHighlightMissingFields(true)}
      content={(
        <DatePickerField
          name="invoice_date"
          label={Strings.deviceInvoices.invoiceDate || "Invoice Date"}
          value={selectedDate}
          onChange={date => setSelectedDate(date)}
          isRequired
          highlightInvalid={highlightInvalidFields}
        />
      )}
    />
  );
}

export function EditPriceModal({ price, onClose, onSubmit, organizations, devices, programs }) {
  const [newPrice, setNewPrice] = useState(price? {
    ...price,
    is_sample: !!price.is_sample,
    effective_from: price.effective_from ? moment(price.effective_from) : null,
    effective_to: price.effective_to ? moment(price.effective_to) : null,
  } : null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [chargeFor, setChargeFor] = useState(price ? (price.device_model_id ? 'device' : 'program') : null);

  const isEditing = !!price?.id;

  React.useEffect(() => {
    const errors = [];
    if (!newPrice) {
      setValidationErrors(errors);
      return;
    }
    if (newPrice.price === '' || newPrice.price === null || newPrice.price === undefined || Number.isNaN(Number(newPrice.price))) {
      errors.push({ property: 'price', propertyName: Strings.deviceInvoices.price, missing: true, errors: [] });
    }
    if (!newPrice.effective_from) {
      errors.push({ property: 'effective_from', propertyName: Strings.effectiveFrom, missing: true, errors: [] });
    }
    if (newPrice.effective_to && moment(newPrice.effective_to).isBefore(moment(newPrice.effective_from))) {
      errors.push({ property: 'effective_to', propertyName: Strings.effectiveTo, missing: false, errors: [Strings.errors.endDateMustBeAfterStartDate] });
    }
    if (!newPrice.pricing_type) {
      errors.push({ property: 'pricing_type', propertyName: Strings.deviceInvoices.pricingType, missing: true, errors: [] });
    } else {
      if (isDeviceBasedPricingType(newPrice.pricing_type) && !newPrice.device_model_id) {
        errors.push({ property: 'device_model_id', propertyName: Strings.device, missing: true, errors: [] });
      }
      if (newPrice.pricing_type === DeviceInvoicesTypes.RECURRING && chargeFor === 'program' && !newPrice.cm_program) {
        errors.push({ property: 'cm_program', propertyName: Strings.program, missing: true, errors: [] });
      }
      if (newPrice.pricing_type === DeviceInvoicesTypes.RECURRING && chargeFor === 'device' && !newPrice.device_model_id) {
        errors.push({ property: 'device_model_id', propertyName: Strings.device, missing: true, errors: [] });
      }
    }
    setValidationErrors(errors);
  }, [newPrice, chargeFor]);

  React.useEffect(() => {
    if (price?.pricing_type === DeviceInvoicesTypes.RECURRING) {
      setChargeFor(price.device_model_id ? 'device' : 'program');
    }
  }, [price]);

  const handleSubmit = async () => {
    setIsSaving(true);
    const success = await onSubmit(newPrice);
    if (!success) {
      setIsSaving(false);
    }
  };

  return (
    <SimpleModal
      name={EDIT_PRICE_MODAL_NAME}
      title={isEditing ? Strings.deviceInvoices.editPrice : Strings.deviceInvoices.addPrice}
      onCancel={onClose}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSaving || !newPrice || validationErrors.length > 0}
      emptyFieldsCount={validationErrors.filter(e => e.missing).length}
      saveButtonTooltip={getTooltipFromValidationErrors(validationErrors)}
      onNextButtonHover={() => setHighlightMissingFields(true)}
      content={(
        <>
          {!isEditing && (
            <>
              <SelectField
                name="pricing_type"
                label={Strings.deviceInvoices.pricingType || 'Pricing Type'}
                value={newPrice?.pricing_type}
                onChange={option => {
                  const pricingType = option.value;
                  const isRecurring = pricingType === DeviceInvoicesTypes.RECURRING;
                  setChargeFor(isRecurring ? 'program' : 'device');
                  setNewPrice({
                    ...newPrice,
                    pricing_type: pricingType,
                    cm_program: isRecurring ? newPrice.cm_program : undefined,
                    device_model_id: isRecurring ? undefined : newPrice.device_model_id,
                  });
                }}
                data={Object.values(DeviceInvoicesTypes).map(pricingType => ({
                  value: pricingType,
                  label: getPricingTypeLabel(pricingType),
                }))}
                isRequired
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={getErrorsForTooltip(validationErrors, 'pricing_type')}
              />
              {newPrice.pricing_type === DeviceInvoicesTypes.RECURRING && (
                <SelectField
                  name="charge_for"
                  label={Strings.chargeFor || 'Charge For'}
                  value={chargeFor}
                  onChange={option => {
                    const newChargeFor = option.value;
                    setChargeFor(newChargeFor);
                    setNewPrice({
                      ...newPrice,
                      cm_program: newChargeFor === 'program' ? newPrice.cm_program : undefined,
                      device_model_id: newChargeFor === 'device' ? newPrice.device_model_id : undefined,
                    });
                  }}
                  data={[
                    { value: 'device', label: Strings.device || 'Device' },
                    { value: 'program', label: Strings.program || 'Program' },
                  ]}
                  isRequired
                  highlightInvalid={highlightInvalidFields}
                />
              )}
              {newPrice.pricing_type === DeviceInvoicesTypes.RECURRING && chargeFor === 'program' && (
                <SelectField
                  name="cm_program"
                  label={Strings.program || 'Program'}
                  value={newPrice?.cm_program}
                  onChange={option => {
                    setNewPrice({ ...newPrice, cm_program: option.value });
                  }}
                  data={programs.map(program => ({ value: program, label: program })) || []}
                  isRequired
                  highlightInvalid={highlightInvalidFields}
                  errorsForTooltip={getErrorsForTooltip(validationErrors, 'cm_program')}
                />
              )}
              {(isDeviceBasedPricingType(newPrice.pricing_type)
                || (newPrice.pricing_type === DeviceInvoicesTypes.RECURRING && chargeFor === 'device')) && (
                <SelectField
                  name="device_model_id"
                  label={Strings.device || 'Device'}
                  value={newPrice?.device_model_id}
                  onChange={option => {
                    setNewPrice({ ...newPrice, device_model_id: option.value });
                  }}
                  data={devices.map(device => ({
                    value: device.id,
                    label: `${device.manufacturer} ${device.model} (${device.device_type})`,
                  })) || []}
                  isSearchable
                  isRequired
                  highlightInvalid={highlightInvalidFields}
                  errorsForTooltip={getErrorsForTooltip(validationErrors, 'device_model_id')}
                />
              )}
            </>
          )}
          <SelectField
            name="is_sample"
            label={Strings.deviceInvoices.sample}
            value={newPrice?.is_sample ? 'yes' : 'no'}
            onChange={option => setNewPrice({ ...newPrice, is_sample: option.value === 'yes' })}
            data={[
              { value: 'no', label: Strings.no },
              { value: 'yes', label: Strings.yes },
            ]}
            isRequired
          />
          <Input
            name="price"
            label={Strings.deviceInvoices.price}
            value={newPrice?.price}
            onChange={e => setNewPrice({ ...newPrice, price: e.target.value })}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={getErrorsForTooltip(validationErrors, 'price')}
          />
          <DatePickerField
            name="effective_from"
            label={Strings.effectiveFrom || "Effective From"}
            value={newPrice?.effective_from}
            onChange={date => setNewPrice({ ...newPrice, effective_from: date })}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={getErrorsForTooltip(validationErrors, 'effective_from')}
          />
          <DatePickerField
            name="effective_to"
            label={Strings.effectiveTo || "Effective To"}
            value={newPrice?.effective_to}
            onChange={date => setNewPrice({ ...newPrice, effective_to: date })}
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={getErrorsForTooltip(validationErrors, 'effective_to')}
          />
          <SelectField
            name="organization"
            label={Strings.deviceInvoices.pricingScope || 'Pricing Scope'}
            value={newPrice?.organization_id}
            onChange={option => {
              setNewPrice({ ...newPrice, organization_id: option.value });
            }}
            data={[{ value: '', label: Strings.deviceInvoices.defaultPrice }].concat(organizations.map(org => ({ value: org.id, label: org.name })) || [])}
            isSearchable
            highlightInvalid={highlightInvalidFields}
          />
        </>
      )}
    />
  );
}

const invoiceShape = PropTypes.shape({
  billing_end_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  billing_status: PropTypes.string,
  invoice_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  provider_id: PropTypes.string,
  shipment_date_from_rxcap: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
});

const priceShape = PropTypes.shape({
  cm_program: PropTypes.string,
  device_model_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  device_model_label: PropTypes.string,
  effective_from: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  effective_to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  historicalPrices: PropTypes.arrayOf(PropTypes.shape({})),
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  is_sample: PropTypes.bool,
  organization_name: PropTypes.string,
  pricing_type: PropTypes.string,
});

const optionListShape = PropTypes.arrayOf(PropTypes.shape({}));

EditInvoiceModal.propTypes = {
  invoice: invoiceShape,
  isRecurring: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  organizations: optionListShape,
};

GenerateRecurringInvoicesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SetInvoiceDateModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string,
};

EditPriceModal.propTypes = {
  price: priceShape,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  organizations: optionListShape,
  devices: optionListShape,
  programs: PropTypes.arrayOf(PropTypes.string),
};

EditInvoiceModal.defaultProps = {
  invoice: null,
  isRecurring: false,
  organizations: [],
};

SetInvoiceDateModal.defaultProps = {
  title: undefined,
};

EditPriceModal.defaultProps = {
  price: null,
  organizations: [],
  devices: [],
  programs: [],
};

export function PriceHistoryModal({ price, onClose }) {
  const graphData = React.useMemo(() => getGraphDataForPriceHistory(price, "price", "date"), [price]);

  return (
    <SimpleModal
      name={PRICE_HISTORY_MODAL_NAME}
      title={Strings.deviceInvoices.priceHistory || "Price History"}
      onCancel={onClose}
      content={(
        <>
          <div>
            {price.device_model_label ? (
              <strong>{`${Strings.deviceModel}: ${price.device_model_label}`}</strong>
            ) : price.cm_program ? (
              <strong>{`${Strings.program}: ${price.cm_program}`}</strong>
            ) : null}
          </div>
          <div>
            {price.organization_name ? (
              <strong>{`${Strings.organization}: ${price.organization_name}`}</strong>
            ) : null}
          </div>
          {price?.historicalPrices ? (
            <>
              <Table3
                columns={getPricingColumns(price.pricing_type, true)}
                data={price.historicalPrices}
                noPagination
                pagination={{limit: price.historicalPrices.length}}
              />
              <div className="graphStandardContainer">
                <GenericLineGraph
                  graphData={graphData}
                  hasBorder
                  forceCondensed
                  xAxisTopPosition={false}
                  xAxisLabelsMinMaxOnly
                  yAxisWidth={40}
                  yAxisUnit="$"
                  tooltipTitle={Strings.deviceInvoices.price}
                  tooltipDateFormat="LL"
                  dataSeries={[{
                    name: 'Price',
                    color: '#47d826',
                    selector: item => item.price,
                  }]}
                  dateSelector={item => item.date}
                  hasReferenceAreas={false}
                  numericXAxis
                />
              </div>
            </>
          ) : Strings.deviceInvoices.noPricesHistory}
        </>
      )}
    />
  );
}

PriceHistoryModal.propTypes = {
  price: priceShape.isRequired,
  onClose: PropTypes.func.isRequired,
};
